import { isDefined } from "@clipboard-health/util-ts";
import { type Shift } from "@src/appV2/Shifts/Shift/types";
import { addMinutes, compareAsc, hoursToMinutes, isAfter, parseISO } from "date-fns";

const HOURS_BEFORE_SHIFT_ELIGIBLE = 2;
const SHIFT_LOOKAHEAD_MINUTES = hoursToMinutes(HOURS_BEFORE_SHIFT_ELIGIBLE);

export interface FindShiftEligibleForTripInput {
  shifts: Shift[];
  currentDate: Date;
}

/**
 * Returns the earliest shift that is still in progress (or upcoming within the two-hour window),
 * not clocked in yet, and eligible for a worker trip record.
 *
 * @param params - The shifts list and current date reference.
 */
export function findShiftEligibleForTrip({
  shifts,
  currentDate,
}: FindShiftEligibleForTripInput): Shift | undefined {
  if (shifts.length === 0) {
    return undefined;
  }

  const cutoff = addMinutes(currentDate, SHIFT_LOOKAHEAD_MINUTES);

  return shifts
    .filter((shift) => {
      const start = parseISO(shift.start);
      const end = parseISO(shift.end);

      if (!isAfter(end, currentDate)) {
        return false;
      }

      if (isDefined(shift.clockInOut?.start)) {
        return false;
      }

      return !isAfter(start, cutoff);
    })
    .sort((a, b) => compareAsc(parseISO(a.start), parseISO(b.start)))?.[0];
}
